import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { Button, Center, Group, Stack, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconChevronLeft } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { BackgroundContext } from "@/contexts/background";
import { fetchLabFlowSheet } from "@/health-gorilla/services/flow-sheet";
import { LabFlowSheetData } from "@/health-gorilla/types/flow-sheet";
import NotFound from "@/NotFound";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";

import ClientLabFlowSheetTable from "./ClientLabFlowSheetTable";

const MAX_WIDTH = "100%";

export default function ClientLabFlowSheet() {
  const { setBackground } = useContext(BackgroundContext);
  const { clientId } = useParams<{ clientId: string }>();
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [labFlowSheetData, setLabFlowSheetData] = useState<LabFlowSheetData | null>(null);
  const { hasLabResultsViewPermission } = usePractitionerPermissions();
  const navigate = useNavigate();
  const medplum = useMedplum();

  const hasAccess = hasLabResultsViewPermission;

  useLayoutEffect(() => {
    setBackground("white");
  }, [setBackground]);

  useEffect(() => {
    if (!clientId || !hasAccess) return;

    (async () => {
      try {
        startLoading();
        const results = await fetchLabFlowSheet(medplum, clientId);
        setLabFlowSheetData(results);
      } finally {
        stopLoading();
      }
    })();
  }, [medplum, clientId, hasAccess, startLoading, stopLoading]);

  const [collectionDates, sections, edd, hasData] = useMemo(() => {
    if (!clientId || !hasAccess || isLoading) return [[], [], undefined, false];
    const collectionDates = labFlowSheetData?.collectionDates ?? [];
    const sections = labFlowSheetData?.sections ?? [];
    const edd = labFlowSheetData?.edd;
    const hasData = sections.length > 0 && collectionDates.length > 0;

    return [collectionDates, sections, edd, hasData];
  }, [labFlowSheetData, clientId, hasAccess, isLoading]);

  if (!hasAccess) {
    return <NotFound />;
  }

  if (isLoading) {
    return <Loading />;
  }

  const handleGoBack = () => {
    navigate(`/clients/${clientId}/results`);
  };

  return (
    <Center m="lg" mt="xl" data-testid="lab-flow-sheet">
      <Stack gap="xl" w={MAX_WIDTH}>
        <Group>
          <Button
            variant="outline"
            color="burgundy.9"
            onClick={handleGoBack}
            data-testid="lab-flow-sheet-back"
            leftSection={<IconChevronLeft />}
          >
            Go Back
          </Button>
          <Title order={1}>Lab Flow Sheet</Title>
        </Group>
        {!hasData ? (
          <Group
            h={200}
            w="100%"
            bg="gray.0"
            align="center"
            justify="center"
            style={{ borderRadius: 8 }}
          >
            <TextWrapper c="gray.7">No labs have been requested for this client.</TextWrapper>
          </Group>
        ) : (
          <ClientLabFlowSheetTable
            edd={edd}
            sections={sections}
            collectionDates={collectionDates}
          />
        )}
      </Stack>
    </Center>
  );
}
